import { type FC, useCallback } from "react";
import { Section, Fonts, Button } from "@snapchat/snap-design-system";
import { generatePath, Link } from "react-router-dom";
import { css } from "@emotion/css";
import { LensModel } from "../models";
import { buttonOffset } from "../../../common/components";
import { VendorDataCollection } from "../../../vendor-data/models";
import { type VendorDataChangedAction } from "../../../vendor-data";
import { GroupInfo } from "../../components";
import { RangeModifier } from "../../../connections/models";
import { type Permissions } from "../../../gql-generated/aliases";
import { ActionDisabledTooltip, DisabledButtonToolipFix } from "../../../common/components/ActionDisabledTooltip";
import { AbsoluteRoute } from "../../../../core/routes";

interface GroupInfoSectionProps {
    name: string;
    groupId: string;
    organizationId: string;
    priority: number;
    vendorDataItems: VendorDataCollection;
    scheduleRange: RangeModifier;
    numberOfLenses: number;
    onScheduleChanged: (scheduleRange: RangeModifier, groupId: string) => void;
    onLensInGroupVendorDataChanged: (event: VendorDataChangedAction, groupId: string) => void;
    onRemoveFromGroup: (groupId: string) => void;
    permissions: Permissions;
    lens: LensModel;
}

const container = css`
    display: inline-block;
`;

export const GroupInfoSection: FC<GroupInfoSectionProps> = ({
    name,
    groupId,
    organizationId,
    priority,
    vendorDataItems,
    scheduleRange,
    numberOfLenses,
    onScheduleChanged,
    onLensInGroupVendorDataChanged,
    onRemoveFromGroup,
    permissions,
    lens,
}) => {
    const handleRemove = useCallback(() => {
        onRemoveFromGroup(groupId);
    }, [groupId, onRemoveFromGroup]);

    return (
        <Section
            title={
                <span data-testid="group-info-item-title" style={Fonts.Heading.H400}>
                    {name}
                </span>
            }
            rightHeaderContent={
                <>
                    <Link to={generatePath(AbsoluteRoute.ORG_LENS_SCHEDULER_GROUP, { groupId, orgId: organizationId })}>
                        <Button>View Group</Button>
                    </Link>
                    <div className={container}>
                        <ActionDisabledTooltip placement="bottom" hasPermission={permissions?.updateGroups ?? false}>
                            <DisabledButtonToolipFix
                                isDisabled={!permissions.updateGroups}
                                onClick={handleRemove}
                                className={buttonOffset}
                            >
                                Remove Lens from Group
                            </DisabledButtonToolipFix>
                        </ActionDisabledTooltip>
                    </div>
                </>
            }
            data-testid="group-info-item"
        >
            <GroupInfo
                groupId={groupId}
                groupName={name}
                priority={priority}
                vendorData={vendorDataItems}
                scheduleRange={scheduleRange}
                numberOfLenses={numberOfLenses}
                onScheduleChanged={onScheduleChanged}
                onVendorDataChanged={onLensInGroupVendorDataChanged}
                permissions={permissions}
                lens={lens}
            />
        </Section>
    );
};
